import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { 
  FileText, Receipt, Truck, ShoppingCart, ClipboardList,
  Palette, Type, LayoutGrid, FileCheck, StickyNote, Save,
  ZoomIn, ZoomOut, RotateCcw, Check, ChevronDown, ChevronRight,
  Printer
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';

// Document Types
const DOCUMENT_TYPES = [
  { id: 'quotation', label: 'Quotation', icon: ClipboardList },
  { id: 'sales_invoice', label: 'Sales Invoice', icon: Receipt },
  { id: 'proforma_invoice', label: 'Proforma Invoice', icon: FileText },
  { id: 'purchase_order', label: 'Purchase Order', icon: ShoppingCart },
  { id: 'delivery_challan', label: 'Delivery Challan', icon: Truck },
];

// Theme Definitions - Each with DISTINCT layout characteristics
const THEMES = [
  { 
    id: 'minimal_professional', 
    name: 'Minimal Professional', 
    description: 'Clean lines with large white space, thin borders',
    bestFor: 'Consultants & Agencies',
    defaultFont: 'inter',
    characteristics: ['Large whitespace', 'Thin borders', 'Compact totals', 'Text-focused header']
  },
  { 
    id: 'retail_dense', 
    name: 'Retail Dense', 
    description: 'Compact layout optimized for many line items',
    bestFor: 'FMCG & Retail Shops',
    defaultFont: 'roboto_condensed',
    characteristics: ['Dense rows', 'Strong grid', 'Batch/Expiry support', 'Boxed totals']
  },
  { 
    id: 'modern_business', 
    name: 'Modern Business', 
    description: 'Balanced design with colored sections',
    bestFor: 'SMBs & Distributors',
    defaultFont: 'poppins',
    characteristics: ['Section cards', 'Colored header band', 'Medium spacing', 'Logo prominent']
  },
  { 
    id: 'classic_elegant', 
    name: 'Classic Elegant', 
    description: 'Traditional formal layout with symmetry',
    bestFor: 'Traditional Businesses',
    defaultFont: 'merriweather',
    characteristics: ['Centered title', 'Serif typography', 'Framed sections', 'Formal borders']
  },
  { 
    id: 'creative_modern', 
    name: 'Creative Modern', 
    description: 'Bold asymmetric design with visual flair',
    bestFor: 'Designers & Creators',
    defaultFont: 'dm_sans',
    characteristics: ['Asymmetric layout', 'Vertical totals', 'Stylized sections', 'Modern typography']
  },
];

// Font Options with actual font families
const FONTS = [
  { id: 'inter', name: 'Inter', family: "'Inter', sans-serif", type: 'sans' },
  { id: 'poppins', name: 'Poppins', family: "'Poppins', sans-serif", type: 'sans' },
  { id: 'roboto', name: 'Roboto', family: "'Roboto', sans-serif", type: 'sans' },
  { id: 'roboto_condensed', name: 'Roboto Condensed', family: "'Roboto Condensed', sans-serif", type: 'condensed' },
  { id: 'dm_sans', name: 'DM Sans', family: "'DM Sans', sans-serif", type: 'sans' },
  { id: 'merriweather', name: 'Merriweather', family: "'Merriweather', serif", type: 'serif' },
  { id: 'playfair', name: 'Playfair Display', family: "'Playfair Display', serif", type: 'serif' },
];

// Color presets
const COLOR_PRESETS = [
  { primary: '#0d9488', secondary: '#1e293b', accent: '#f59e0b', name: 'Teal' },
  { primary: '#3b82f6', secondary: '#1f2937', accent: '#10b981', name: 'Blue' },
  { primary: '#8b5cf6', secondary: '#374151', accent: '#f43f5e', name: 'Purple' },
  { primary: '#0f766e', secondary: '#1e293b', accent: '#ca8a04', name: 'Forest' },
  { primary: '#dc2626', secondary: '#18181b', accent: '#facc15', name: 'Red' },
  { primary: '#2563eb', secondary: '#0f172a', accent: '#f97316', name: 'Royal' },
];

// Default Configuration
const getDefaultConfig = () => ({
  theme: 'minimal_professional',
  blackAndWhite: false,
  colors: {
    primary: '#0d9488',
    secondary: '#1e293b',
    accent: '#f59e0b',
  },
  typography: {
    fontFamily: 'inter',
    baseFontSize: 'normal',
    headingSize: 'normal',
  },
  content: {
    showCustomerPhone: true,
    showCustomerEmail: true,
    showBusinessGST: true,
    showItemDescription: true,
    showHSN: true,
    showBatchNumber: false,
    showExpiryDate: false,
    showMfgDate: false,
    showAlternateUnit: false,
    showFreeQuantity: false,
    showDiscountColumn: true,
    showTaxBreakup: true,
    showTotalInWords: true,
    showSignatureSection: true,
    showQRCode: false,
    showPaymentDetails: true,
  },
  layout: {
    logoPosition: 'left',
    footerLayout: 'compact',
  },
  notes: {
    defaultNotes: 'Thank you for your business!',
    termsAndConditions: '1. Goods once sold will not be taken back.\n2. Payment is due within 30 days.',
  },
});

const TemplateStudioPage = () => {
  const { businessId } = useParams();
  const [activeDocument, setActiveDocument] = useState('sales_invoice');
  const [config, setConfig] = useState(getDefaultConfig());
  const [expandedSections, setExpandedSections] = useState(['theme']);
  const [zoom, setZoom] = useState(85);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);

  useEffect(() => {
    loadConfiguration();
  }, [activeDocument]);

  const loadConfiguration = async () => {
    try {
      const { data } = await api.get(`/templates/${activeDocument}`);
      if (data.config) setConfig(data.config);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load template configuration'));
    }
  };

  const saveConfiguration = async () => {
    setSaving(true);
    try {
      await api.put(`/templates/${activeDocument}`, { config });
      toast.success('Template saved successfully!');
      setHasChanges(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save template'));
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (path, value) => {
    setConfig(prev => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let current = newConfig;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newConfig;
    });
    setHasChanges(true);
  };

  const selectTheme = (themeId) => {
    const theme = THEMES.find(t => t.id === themeId);
    if (theme) {
      setConfig(prev => ({
        ...prev,
        theme: themeId,
        typography: {
          ...prev.typography,
          fontFamily: theme.defaultFont,
        }
      }));
      setHasChanges(true);
    }
  };

  const resetToDefault = () => {
    setConfirmModal({
      title: 'Reset to Default',
      description: 'Reset all template settings to default? Any unsaved changes will be lost.',
      confirmLabel: 'Reset',
      confirmClass: 'bg-amber-600 hover:bg-amber-700',
      icon: RotateCcw,
      iconBgClass: 'bg-amber-100',
      iconClass: 'text-amber-600',
      onConfirm: () => {
        setConfirmModal(null);
        setConfig(getDefaultConfig());
        setHasChanges(true);
      },
    });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => 
      prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]
    );
  };

  return (
    <div className="h-full flex flex-col bg-[#F7F8FA]">
      {/* Compact Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-5 py-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-[15px] font-medium text-slate-800">Template Studio</h1>
              <p className="text-[11px] text-slate-400">Customize invoice templates</p>
            </div>
            {/* Inline Document Type Tabs */}
            <div className="flex items-center gap-1 ml-4 border-l border-slate-200 pl-4">
              {DOCUMENT_TYPES.map(doc => {
                const Icon = doc.icon;
                const isActive = activeDocument === doc.id;
                return (
                  <button
                    key={doc.id}
                    onClick={() => setActiveDocument(doc.id)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md transition-all text-[11px] ${
                      isActive ? 'bg-teal-50 text-teal-700 font-medium' : 'text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-teal-600' : 'text-slate-400'}`} />
                    {doc.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Compact B&W Toggle */}
            <button
              onClick={() => updateConfig('blackAndWhite', !config.blackAndWhite)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md border transition-all text-[11px] ${
                config.blackAndWhite 
                  ? 'bg-slate-800 border-slate-800 text-white' 
                  : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
            >
              <Printer className="w-3.5 h-3.5" />
              B&W
            </button>
            <button onClick={resetToDefault} className="px-3 py-1.5 text-slate-500 hover:bg-slate-50 rounded-md text-[11px]">
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={saveConfiguration}
              disabled={saving || !hasChanges}
              className={`px-4 py-1.5 rounded-md text-[11px] font-medium transition-all ${
                hasChanges ? 'bg-teal-600 text-white hover:bg-teal-700' : 'bg-slate-100 text-slate-400 cursor-not-allowed'
              }`}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content - Split Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Controls (38%) */}
        <div className="w-[38%] border-r border-slate-200 bg-white overflow-y-auto">
          <div className="p-4 space-y-3">
            
            {/* Theme Selection - Compact */}
            <ControlSection
              title="Layout Theme"
              icon={LayoutGrid}
              expanded={expandedSections.includes('theme')}
              onToggle={() => toggleSection('theme')}
            >
              <div className="space-y-2">
                {THEMES.map(theme => (
                  <button
                    key={theme.id}
                    onClick={() => selectTheme(theme.id)}
                    className={`w-full p-3 rounded-lg border transition-all text-left group ${
                      config.theme === theme.id 
                        ? 'border-teal-400 bg-teal-50/60 shadow-sm shadow-teal-100' 
                        : 'border-slate-150 hover:border-slate-250 hover:shadow-sm bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        {config.theme === theme.id && (
                          <span className="w-4 h-4 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <Check className="w-2.5 h-2.5 text-white" />
                          </span>
                        )}
                        <div>
                          <span className="text-[12px] font-medium text-slate-700 block">{theme.name}</span>
                          <span className="text-[10px] text-slate-400">{theme.bestFor}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2 pl-6">
                      {theme.characteristics.slice(0, 3).map((char, i) => (
                        <span key={i} className="px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded text-[9px]">
                          {char}
                        </span>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            </ControlSection>

            {/* Color Customization */}
            <ControlSection
              title="Color Scheme"
              icon={Palette}
              expanded={expandedSections.includes('colors')}
              onToggle={() => toggleSection('colors')}
              badge="Overlay"
              disabled={config.blackAndWhite}
            >
              {config.blackAndWhite ? (
                <p className="text-[12px] text-slate-500 italic">Colors disabled in Black & White mode</p>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="text-[12px] font-medium text-slate-600 mb-2 block">Quick Presets</label>
                    <div className="flex flex-wrap gap-2">
                      {COLOR_PRESETS.map((preset, i) => (
                        <button
                          key={i}
                          onClick={() => {
                            updateConfig('colors.primary', preset.primary);
                            updateConfig('colors.secondary', preset.secondary);
                            updateConfig('colors.accent', preset.accent);
                          }}
                          className="flex items-center gap-1 px-2 py-1 border border-slate-200 rounded-lg hover:border-slate-300"
                          title={preset.name}
                        >
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.primary }}></div>
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.accent }}></div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <ColorPicker label="Primary Color" value={config.colors.primary} onChange={(c) => updateConfig('colors.primary', c)} />
                  <ColorPicker label="Secondary Color" value={config.colors.secondary} onChange={(c) => updateConfig('colors.secondary', c)} />
                  <ColorPicker label="Accent Color" value={config.colors.accent} onChange={(c) => updateConfig('colors.accent', c)} />
                </div>
              )}
            </ControlSection>

            {/* Typography */}
            <ControlSection
              title="Typography"
              icon={Type}
              expanded={expandedSections.includes('typography')}
              onToggle={() => toggleSection('typography')}
            >
              <div className="space-y-4">
                <div>
                  <label className="text-[12px] font-medium text-slate-600 mb-2 block">Font Family</label>
                  <div className="grid grid-cols-2 gap-2">
                    {FONTS.map(font => (
                      <button
                        key={font.id}
                        onClick={() => updateConfig('typography.fontFamily', font.id)}
                        className={`px-3 py-2 rounded-lg border text-[12px] font-medium transition-all ${
                          config.typography.fontFamily === font.id
                            ? 'border-teal-500 bg-teal-50 text-teal-700'
                            : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                        style={{ fontFamily: font.family }}
                      >
                        {font.name}
                        <span className="block text-[9px] opacity-60">{font.type}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[12px] font-medium text-slate-600 mb-2 block">Font Size</label>
                  <div className="flex gap-2">
                    {['small', 'normal', 'large'].map(size => (
                      <button
                        key={size}
                        onClick={() => updateConfig('typography.baseFontSize', size)}
                        className={`flex-1 px-3 py-2 rounded-lg border text-[12px] font-medium capitalize ${
                          config.typography.baseFontSize === size
                            ? 'border-teal-500 bg-teal-50 text-teal-700'
                            : 'border-slate-200 text-slate-600'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </ControlSection>

            {/* Content Controls */}
            <ControlSection
              title="Document Content"
              icon={FileCheck}
              expanded={expandedSections.includes('content')}
              onToggle={() => toggleSection('content')}
            >
              <div className="space-y-2">
                {[
                  { key: 'showCustomerPhone', label: 'Customer phone number' },
                  { key: 'showCustomerEmail', label: 'Customer email' },
                  { key: 'showBusinessGST', label: 'Business GST / Tax ID' },
                  { key: 'showItemDescription', label: 'Item description' },
                  { key: 'showHSN', label: 'HSN / SAC code' },
                  { key: 'showBatchNumber', label: 'Batch number' },
                  { key: 'showExpiryDate', label: 'Expiry date' },
                  { key: 'showDiscountColumn', label: 'Discount column' },
                  { key: 'showTaxBreakup', label: 'Tax breakup section' },
                  { key: 'showTotalInWords', label: 'Total in words' },
                  { key: 'showSignatureSection', label: 'Signature section' },
                  { key: 'showQRCode', label: 'QR code' },
                  { key: 'showPaymentDetails', label: 'Payment details' },
                ].map(item => (
                  <ContentToggle
                    key={item.key}
                    label={item.label}
                    checked={config.content[item.key]}
                    onChange={(v) => updateConfig(`content.${item.key}`, v)}
                  />
                ))}
              </div>
            </ControlSection>

            {/* Notes & Terms */}
            <ControlSection
              title="Notes & Terms"
              icon={StickyNote}
              expanded={expandedSections.includes('notes')}
              onToggle={() => toggleSection('notes')}
            >
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-1">
                    <label className="text-[12px] font-medium text-slate-600">Default Notes</label>
                    <span className="text-[10px] text-slate-400">{config.notes.defaultNotes.length}/300</span>
                  </div>
                  <textarea
                    value={config.notes.defaultNotes}
                    onChange={(e) => updateConfig('notes.defaultNotes', e.target.value.slice(0, 300))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[12px] resize-none focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                    rows={2}
                  />
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <label className="text-[12px] font-medium text-slate-600">Terms & Conditions</label>
                    <span className="text-[10px] text-slate-400">{config.notes.termsAndConditions.length}/500</span>
                  </div>
                  <textarea
                    value={config.notes.termsAndConditions}
                    onChange={(e) => updateConfig('notes.termsAndConditions', e.target.value.slice(0, 500))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[12px] resize-none focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                    rows={3}
                  />
                </div>
              </div>
            </ControlSection>

          </div>
        </div>

        {/* Right Panel - Live Preview (62%) */}
        <div className="w-[62%] bg-slate-50 flex flex-col">
          <div className="flex-shrink-0 bg-white border-b border-slate-100 px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-slate-500">Preview</span>
              {config.blackAndWhite && (
                <span className="px-1.5 py-0.5 bg-slate-700 text-white rounded text-[9px]">B&W</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setZoom(Math.max(50, zoom - 15))} className="p-1 hover:bg-slate-100 rounded" disabled={zoom <= 50}>
                <ZoomOut className="w-3.5 h-3.5 text-slate-400" />
              </button>
              <span className="text-[10px] text-slate-500 min-w-[32px] text-center">{zoom}%</span>
              <button onClick={() => setZoom(Math.min(120, zoom + 15))} className="p-1 hover:bg-slate-100 rounded" disabled={zoom >= 120}>
                <ZoomIn className="w-3.5 h-3.5 text-slate-400" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-auto p-4 flex justify-center">
            <div style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top center' }}>
              <InvoicePreview config={config} documentType={activeDocument} />
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconBgClass={confirmModal?.iconBgClass}
        iconClass={confirmModal?.iconClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
};

// Control Section Component - Compact
const ControlSection = ({ title, icon: Icon, expanded, onToggle, children, badge, disabled }) => (
  <div className={`border rounded-lg overflow-hidden transition-shadow ${disabled ? 'opacity-50' : ''} ${expanded ? 'border-slate-200 shadow-sm' : 'border-slate-150'}`}>
    <button onClick={onToggle} className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-slate-50/50 transition-colors bg-white">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-md bg-slate-100 flex items-center justify-center">
          <Icon className="w-3.5 h-3.5 text-slate-500" />
        </div>
        <span className="text-[12px] font-medium text-slate-700">{title}</span>
        {badge && <span className="px-1 py-0.5 bg-slate-100 text-slate-400 rounded text-[8px]">{badge}</span>}
      </div>
      {expanded ? <ChevronDown className="w-4 h-4 text-slate-300" /> : <ChevronRight className="w-4 h-4 text-slate-300" />}
    </button>
    {expanded && <div className="px-3 pb-3 border-t border-slate-100 pt-3 bg-white">{children}</div>}
  </div>
);

// Color Picker
const ColorPicker = ({ label, value, onChange }) => (
  <div className="flex items-center justify-between">
    <span className="text-[12px] text-slate-600">{label}</span>
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 rounded-lg border border-slate-200 overflow-hidden">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-10 h-10 -m-1 cursor-pointer" />
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-20 px-2 py-1 border border-slate-200 rounded text-[11px] font-mono uppercase"
      />
    </div>
  </div>
);

// Content Toggle
const ContentToggle = ({ label, checked, onChange }) => (
  <label className="flex items-center justify-between py-1.5 cursor-pointer group">
    <span className="text-[12px] text-slate-600 group-hover:text-slate-800">{label}</span>
    <button
      onClick={() => onChange(!checked)}
      className={`relative w-9 h-5 rounded-full transition-colors ${checked ? 'bg-teal-500' : 'bg-slate-200'}`}
    >
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${checked ? 'left-4' : 'left-0.5'}`} />
    </button>
  </label>
);

// =====================================================
// INVOICE PREVIEW COMPONENT - 5 DISTINCT LAYOUTS
// =====================================================
const InvoicePreview = ({ config, documentType }) => {
  const docLabels = {
    quotation: 'QUOTATION',
    sales_invoice: 'TAX INVOICE',
    proforma_invoice: 'PROFORMA INVOICE',
    purchase_order: 'PURCHASE ORDER',
    delivery_challan: 'DELIVERY CHALLAN',
  };

  const fontFamily = FONTS.find(f => f.id === config.typography.fontFamily)?.family || "'Inter', sans-serif";
  const baseFontSize = { small: '10px', normal: '11px', large: '12px' }[config.typography.baseFontSize];
  
  const bw = config.blackAndWhite;
  const colors = bw 
    ? { primary: '#1f2937', secondary: '#374151', accent: '#4b5563', bg: '#f9fafb' }
    : { primary: config.colors.primary, secondary: config.colors.secondary, accent: config.colors.accent, bg: '#ffffff' };

  const sampleItems = [
    { name: 'Premium Widget Pro', desc: 'High-quality widget with warranty', hsn: '8471', batch: 'BT2024001', expiry: '12/2025', qty: 5, rate: 2499, discount: 10, tax: 18 },
    { name: 'Standard Component X', desc: 'Reliable component for daily use', hsn: '8473', batch: 'BT2024002', expiry: '06/2025', qty: 10, rate: 899, discount: 5, tax: 18 },
    { name: 'Basic Service Pack', desc: 'Annual service package', hsn: '9983', batch: '-', expiry: '-', qty: 2, rate: 4999, discount: 0, tax: 18 },
  ];

  const subtotal = sampleItems.reduce((sum, item) => sum + (item.qty * item.rate * (1 - item.discount / 100)), 0);
  const taxAmount = subtotal * 0.18;
  const total = subtotal + taxAmount;

  // Render based on theme
  switch (config.theme) {
    case 'minimal_professional':
      return <MinimalProfessionalLayout config={config} colors={colors} fontFamily={fontFamily} baseFontSize={baseFontSize} docLabel={docLabels[documentType]} items={sampleItems} subtotal={subtotal} taxAmount={taxAmount} total={total} bw={bw} />;
    case 'retail_dense':
      return <RetailDenseLayout config={config} colors={colors} fontFamily={fontFamily} baseFontSize={baseFontSize} docLabel={docLabels[documentType]} items={sampleItems} subtotal={subtotal} taxAmount={taxAmount} total={total} bw={bw} />;
    case 'modern_business':
      return <ModernBusinessLayout config={config} colors={colors} fontFamily={fontFamily} baseFontSize={baseFontSize} docLabel={docLabels[documentType]} items={sampleItems} subtotal={subtotal} taxAmount={taxAmount} total={total} bw={bw} />;
    case 'classic_elegant':
      return <ClassicElegantLayout config={config} colors={colors} fontFamily={fontFamily} baseFontSize={baseFontSize} docLabel={docLabels[documentType]} items={sampleItems} subtotal={subtotal} taxAmount={taxAmount} total={total} bw={bw} />;
    case 'creative_modern':
      return <CreativeModernLayout config={config} colors={colors} fontFamily={fontFamily} baseFontSize={baseFontSize} docLabel={docLabels[documentType]} items={sampleItems} subtotal={subtotal} taxAmount={taxAmount} total={total} bw={bw} />;
    default:
      return <MinimalProfessionalLayout config={config} colors={colors} fontFamily={fontFamily} baseFontSize={baseFontSize} docLabel={docLabels[documentType]} items={sampleItems} subtotal={subtotal} taxAmount={taxAmount} total={total} bw={bw} />;
  }
};

// =====================================================
// THEME 1: MINIMAL PROFESSIONAL
// Clean, large whitespace, thin borders, text-focused
// =====================================================
const MinimalProfessionalLayout = ({ config, colors, fontFamily, baseFontSize, docLabel, items, subtotal, taxAmount, total, bw }) => (
  <div style={{ width: '595px', minHeight: '842px', fontFamily, fontSize: baseFontSize, backgroundColor: '#fff', padding: '48px', color: colors.secondary }}>
    {/* Header - Text focused, minimal */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '40px' }}>
      <div>
        <h1 style={{ fontSize: '20px', fontWeight: '600', color: colors.secondary, margin: 0 }}>Billsera Business</h1>
        <p style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px' }}>123 Business Street, City - 400001</p>
        {config.content.showBusinessGST && <p style={{ fontSize: '10px', color: '#6b7280' }}>GSTIN: 27XXXXX1234X1ZX</p>}
      </div>
      <div style={{ textAlign: 'right' }}>
        <p style={{ fontSize: '11px', fontWeight: '600', color: colors.primary, letterSpacing: '2px' }}>{docLabel}</p>
        <p style={{ fontSize: '10px', color: '#6b7280', marginTop: '8px' }}>No: INV-2024-001234</p>
        <p style={{ fontSize: '10px', color: '#6b7280' }}>Date: 27 Dec 2024</p>
      </div>
    </div>

    {/* Bill To - Simple */}
    <div style={{ marginBottom: '32px' }}>
      <p style={{ fontSize: '9px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Bill To</p>
      <p style={{ fontSize: '13px', fontWeight: '600', color: colors.secondary }}>ABC Enterprises Pvt. Ltd.</p>
      <p style={{ fontSize: '10px', color: '#6b7280' }}>456 Customer Road, Mumbai - 400002</p>
      {config.content.showCustomerPhone && <p style={{ fontSize: '10px', color: '#6b7280' }}>Phone: +91 98765 43210</p>}
      {config.content.showCustomerEmail && <p style={{ fontSize: '10px', color: '#6b7280' }}>Email: accounts@abc.com</p>}
    </div>

    {/* Table - Thin borders, light separators */}
    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
      <thead>
        <tr style={{ borderBottom: `1px solid ${bw ? '#d1d5db' : colors.primary}20` }}>
          <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: '9px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>#</th>
          <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: '9px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Description</th>
          {config.content.showHSN && <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: '9px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>HSN</th>}
          <th style={{ padding: '10px 8px', textAlign: 'center', fontSize: '9px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Qty</th>
          <th style={{ padding: '10px 8px', textAlign: 'right', fontSize: '9px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Rate</th>
          {config.content.showDiscountColumn && <th style={{ padding: '10px 8px', textAlign: 'center', fontSize: '9px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Disc</th>}
          <th style={{ padding: '10px 8px', textAlign: 'right', fontSize: '9px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Amount</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item, idx) => {
          const amount = item.qty * item.rate * (1 - item.discount / 100);
          return (
            <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '12px 8px', color: '#9ca3af' }}>{idx + 1}</td>
              <td style={{ padding: '12px 8px' }}>
                <p style={{ fontWeight: '500', color: colors.secondary }}>{item.name}</p>
                {config.content.showItemDescription && <p style={{ fontSize: '9px', color: '#9ca3af', marginTop: '2px' }}>{item.desc}</p>}
              </td>
              {config.content.showHSN && <td style={{ padding: '12px 8px', color: '#6b7280' }}>{item.hsn}</td>}
              <td style={{ padding: '12px 8px', textAlign: 'center', color: '#6b7280' }}>{item.qty}</td>
              <td style={{ padding: '12px 8px', textAlign: 'right', color: '#6b7280' }}>₹{item.rate.toLocaleString()}</td>
              {config.content.showDiscountColumn && <td style={{ padding: '12px 8px', textAlign: 'center', color: '#6b7280' }}>{item.discount}%</td>}
              <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '500', color: colors.secondary }}>₹{amount.toLocaleString()}</td>
            </tr>
          );
        })}
      </tbody>
    </table>

    {/* Totals - Compact, right aligned */}
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
      <div style={{ width: '200px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f3f4f6' }}>
          <span style={{ color: '#6b7280', fontSize: '10px' }}>Subtotal</span>
          <span>₹{subtotal.toLocaleString()}</span>
        </div>
        {config.content.showTaxBreakup && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ color: '#6b7280', fontSize: '10px' }}>GST (18%)</span>
            <span>₹{taxAmount.toLocaleString()}</span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', marginTop: '4px' }}>
          <span style={{ fontWeight: '600' }}>Total</span>
          <span style={{ fontWeight: '700', fontSize: '14px', color: colors.primary }}>₹{total.toLocaleString()}</span>
        </div>
      </div>
    </div>

    {config.content.showTotalInWords && (
      <p style={{ fontSize: '9px', color: '#6b7280', fontStyle: 'italic', marginBottom: '20px' }}>
        Amount in words: Rupees Twenty Six Thousand Four Hundred Seventy Nine Only
      </p>
    )}

    {/* Footer - Minimal */}
    {config.content.showPaymentDetails && (
      <div style={{ padding: '12px', backgroundColor: bw ? '#f9fafb' : `${colors.primary}08`, borderRadius: '4px', marginBottom: '20px' }}>
        <p style={{ fontSize: '9px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Payment Details</p>
        <p style={{ fontSize: '9px', color: '#6b7280' }}>Bank: HDFC Bank | A/C: 1234567890 | IFSC: HDFC0001234</p>
      </div>
    )}

    <p style={{ fontSize: '10px', color: '#9ca3af', textAlign: 'center' }}>{config.notes.defaultNotes}</p>

    {config.content.showSignatureSection && (
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '40px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '120px', borderTop: '1px solid #d1d5db', paddingTop: '8px' }}>
            <p style={{ fontSize: '9px', color: '#9ca3af' }}>Authorized Signature</p>
          </div>
        </div>
      </div>
    )}
  </div>
);

// =====================================================
// THEME 2: RETAIL DENSE
// Dense rows, strong grid, batch/expiry support
// =====================================================
const RetailDenseLayout = ({ config, colors, fontFamily, baseFontSize, docLabel, items, subtotal, taxAmount, total, bw }) => (
  <div style={{ width: '595px', minHeight: '842px', fontFamily, fontSize: baseFontSize, backgroundColor: '#fff', padding: '24px', color: colors.secondary }}>
    {/* Header - Compact with strong borders */}
    <div style={{ border: `2px solid ${bw ? '#374151' : colors.primary}`, padding: '12px', marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '16px', fontWeight: '700', margin: 0, color: bw ? '#1f2937' : colors.primary }}>BILLSERA BUSINESS</h1>
          <p style={{ fontSize: '9px', color: '#6b7280', margin: '2px 0 0' }}>123 Business Street, City - 400001 | GSTIN: 27XXXXX1234X1ZX</p>
        </div>
        <div style={{ backgroundColor: bw ? '#1f2937' : colors.primary, color: '#fff', padding: '8px 16px', fontWeight: '700', fontSize: '12px' }}>
          {docLabel}
        </div>
      </div>
    </div>

    {/* Invoice Details & Customer - Side by side in boxes */}
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
      <div style={{ border: '1px solid #d1d5db', padding: '10px' }}>
        <p style={{ fontSize: '8px', fontWeight: '700', color: bw ? '#374151' : colors.primary, borderBottom: '1px solid #e5e7eb', paddingBottom: '4px', marginBottom: '6px' }}>BILL TO</p>
        <p style={{ fontSize: '11px', fontWeight: '600' }}>ABC Enterprises Pvt. Ltd.</p>
        <p style={{ fontSize: '9px', color: '#6b7280' }}>456 Customer Road, Mumbai - 400002</p>
        {config.content.showCustomerPhone && <p style={{ fontSize: '9px', color: '#6b7280' }}>Ph: +91 98765 43210</p>}
      </div>
      <div style={{ border: '1px solid #d1d5db', padding: '10px' }}>
        <p style={{ fontSize: '8px', fontWeight: '700', color: bw ? '#374151' : colors.primary, borderBottom: '1px solid #e5e7eb', paddingBottom: '4px', marginBottom: '6px' }}>INVOICE DETAILS</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', fontSize: '9px' }}>
          <span style={{ color: '#6b7280' }}>Invoice No:</span><span style={{ fontWeight: '600' }}>INV-2024-001234</span>
          <span style={{ color: '#6b7280' }}>Date:</span><span style={{ fontWeight: '600' }}>27/12/2024</span>
          <span style={{ color: '#6b7280' }}>Due Date:</span><span style={{ fontWeight: '600' }}>26/01/2025</span>
        </div>
      </div>
    </div>

    {/* Table - Dense with strong grid */}
    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px', border: '1px solid #374151' }}>
      <thead>
        <tr style={{ backgroundColor: bw ? '#374151' : colors.primary, color: '#fff' }}>
          <th style={{ padding: '6px 4px', fontSize: '8px', fontWeight: '700', borderRight: '1px solid #4b5563' }}>#</th>
          <th style={{ padding: '6px 4px', fontSize: '8px', fontWeight: '700', textAlign: 'left', borderRight: '1px solid #4b5563' }}>ITEM</th>
          {config.content.showHSN && <th style={{ padding: '6px 4px', fontSize: '8px', fontWeight: '700', borderRight: '1px solid #4b5563' }}>HSN</th>}
          {config.content.showBatchNumber && <th style={{ padding: '6px 4px', fontSize: '8px', fontWeight: '700', borderRight: '1px solid #4b5563' }}>BATCH</th>}
          {config.content.showExpiryDate && <th style={{ padding: '6px 4px', fontSize: '8px', fontWeight: '700', borderRight: '1px solid #4b5563' }}>EXP</th>}
          <th style={{ padding: '6px 4px', fontSize: '8px', fontWeight: '700', borderRight: '1px solid #4b5563' }}>QTY</th>
          <th style={{ padding: '6px 4px', fontSize: '8px', fontWeight: '700', borderRight: '1px solid #4b5563' }}>RATE</th>
          {config.content.showDiscountColumn && <th style={{ padding: '6px 4px', fontSize: '8px', fontWeight: '700', borderRight: '1px solid #4b5563' }}>DISC</th>}
          <th style={{ padding: '6px 4px', fontSize: '8px', fontWeight: '700' }}>AMT</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item, idx) => {
          const amount = item.qty * item.rate * (1 - item.discount / 100);
          return (
            <tr key={idx} style={{ borderBottom: '1px solid #d1d5db' }}>
              <td style={{ padding: '5px 4px', textAlign: 'center', fontSize: '9px', borderRight: '1px solid #e5e7eb' }}>{idx + 1}</td>
              <td style={{ padding: '5px 4px', fontSize: '9px', fontWeight: '500', borderRight: '1px solid #e5e7eb' }}>{item.name}</td>
              {config.content.showHSN && <td style={{ padding: '5px 4px', textAlign: 'center', fontSize: '9px', borderRight: '1px solid #e5e7eb' }}>{item.hsn}</td>}
              {config.content.showBatchNumber && <td style={{ padding: '5px 4px', textAlign: 'center', fontSize: '8px', borderRight: '1px solid #e5e7eb' }}>{item.batch}</td>}
              {config.content.showExpiryDate && <td style={{ padding: '5px 4px', textAlign: 'center', fontSize: '8px', borderRight: '1px solid #e5e7eb' }}>{item.expiry}</td>}
              <td style={{ padding: '5px 4px', textAlign: 'center', fontSize: '9px', borderRight: '1px solid #e5e7eb' }}>{item.qty}</td>
              <td style={{ padding: '5px 4px', textAlign: 'right', fontSize: '9px', borderRight: '1px solid #e5e7eb' }}>{item.rate}</td>
              {config.content.showDiscountColumn && <td style={{ padding: '5px 4px', textAlign: 'center', fontSize: '9px', borderRight: '1px solid #e5e7eb' }}>{item.discount}%</td>}
              <td style={{ padding: '5px 4px', textAlign: 'right', fontSize: '9px', fontWeight: '600' }}>{amount.toLocaleString()}</td>
            </tr>
          );
        })}
      </tbody>
    </table>

    {/* Totals - Boxed clearly */}
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
      <div style={{ width: '220px', border: '2px solid #374151' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', borderBottom: '1px solid #d1d5db', fontSize: '10px' }}>
          <span>Subtotal</span><span>₹{subtotal.toLocaleString()}</span>
        </div>
        {config.content.showTaxBreakup && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 10px', borderBottom: '1px solid #d1d5db', fontSize: '9px', color: '#6b7280' }}>
              <span>CGST (9%)</span><span>₹{(taxAmount/2).toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 10px', borderBottom: '1px solid #d1d5db', fontSize: '9px', color: '#6b7280' }}>
              <span>SGST (9%)</span><span>₹{(taxAmount/2).toLocaleString()}</span>
            </div>
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', backgroundColor: bw ? '#374151' : colors.primary, color: '#fff', fontWeight: '700', fontSize: '12px' }}>
          <span>GRAND TOTAL</span><span>₹{total.toLocaleString()}</span>
        </div>
      </div>
    </div>

    {config.content.showTotalInWords && (
      <p style={{ fontSize: '9px', color: '#6b7280', marginBottom: '12px' }}>
        <strong>In Words:</strong> Rupees Twenty Six Thousand Four Hundred Seventy Nine Only
      </p>
    )}

    {/* Footer */}
    <div style={{ borderTop: '2px solid #d1d5db', paddingTop: '12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
      <div style={{ fontSize: '8px', color: '#6b7280' }}>
        <p style={{ fontWeight: '600', marginBottom: '4px' }}>Terms:</p>
        <p>{config.notes.termsAndConditions}</p>
      </div>
      {config.content.showSignatureSection && (
        <div style={{ textAlign: 'right' }}>
          <div style={{ display: 'inline-block', textAlign: 'center' }}>
            <div style={{ width: '100px', borderTop: '1px solid #374151', paddingTop: '4px', marginTop: '30px' }}>
              <p style={{ fontSize: '8px', color: '#6b7280' }}>Authorized Sign</p>
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
);

// =====================================================
// THEME 3: MODERN BUSINESS
// Section cards, colored header band, balanced
// =====================================================
const ModernBusinessLayout = ({ config, colors, fontFamily, baseFontSize, docLabel, items, subtotal, taxAmount, total, bw }) => (
  <div style={{ width: '595px', minHeight: '842px', fontFamily, fontSize: baseFontSize, backgroundColor: '#fff', color: colors.secondary }}>
    {/* Colored Header Band */}
    <div style={{ backgroundColor: bw ? '#1f2937' : colors.primary, padding: '24px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div style={{ width: '50px', height: '50px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: '#fff', fontSize: '18px', fontWeight: '700' }}>TL</span>
        </div>
        <div>
          <h1 style={{ color: '#fff', fontSize: '18px', fontWeight: '600', margin: 0 }}>Billsera Business</h1>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '10px', margin: '4px 0 0' }}>123 Business Street, City</p>
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <p style={{ color: '#fff', fontSize: '14px', fontWeight: '700', letterSpacing: '1px' }}>{docLabel}</p>
        <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '10px', marginTop: '4px' }}>#INV-2024-001234</p>
      </div>
    </div>

    <div style={{ padding: '24px 32px' }}>
      {/* Section Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
        <div style={{ backgroundColor: '#f8fafc', borderRadius: '8px', padding: '16px', border: '1px solid #e2e8f0' }}>
          <p style={{ fontSize: '10px', color: bw ? '#374151' : colors.primary, fontWeight: '600', textTransform: 'uppercase', marginBottom: '8px' }}>Bill To</p>
          <p style={{ fontSize: '13px', fontWeight: '600', color: colors.secondary }}>ABC Enterprises Pvt. Ltd.</p>
          <p style={{ fontSize: '10px', color: '#64748b', marginTop: '4px' }}>456 Customer Road, Business Park</p>
          <p style={{ fontSize: '10px', color: '#64748b' }}>Mumbai, Maharashtra - 400002</p>
          {config.content.showCustomerPhone && <p style={{ fontSize: '10px', color: '#64748b', marginTop: '4px' }}>📞 +91 98765 43210</p>}
          {config.content.showCustomerEmail && <p style={{ fontSize: '10px', color: '#64748b' }}>✉️ accounts@abc.com</p>}
        </div>
        <div style={{ backgroundColor: '#f8fafc', borderRadius: '8px', padding: '16px', border: '1px solid #e2e8f0' }}>
          <p style={{ fontSize: '10px', color: bw ? '#374151' : colors.primary, fontWeight: '600', textTransform: 'uppercase', marginBottom: '8px' }}>Invoice Details</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: '10px' }}>
            <span style={{ color: '#64748b' }}>Invoice Date</span><span style={{ fontWeight: '500' }}>27 Dec 2024</span>
            <span style={{ color: '#64748b' }}>Due Date</span><span style={{ fontWeight: '500' }}>26 Jan 2025</span>
            <span style={{ color: '#64748b' }}>Place of Supply</span><span style={{ fontWeight: '500' }}>Maharashtra</span>
            {config.content.showBusinessGST && <><span style={{ color: '#64748b' }}>GSTIN</span><span style={{ fontWeight: '500' }}>27XXXXX1234X1ZX</span></>}
          </div>
        </div>
      </div>

      {/* Table with rounded corners */}
      <div style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: bw ? '#f1f5f9' : `${colors.primary}10` }}>
              <th style={{ padding: '12px', textAlign: 'left', fontSize: '10px', fontWeight: '600', color: bw ? '#374151' : colors.primary }}>#</th>
              <th style={{ padding: '12px', textAlign: 'left', fontSize: '10px', fontWeight: '600', color: bw ? '#374151' : colors.primary }}>Item Description</th>
              {config.content.showHSN && <th style={{ padding: '12px', textAlign: 'left', fontSize: '10px', fontWeight: '600', color: bw ? '#374151' : colors.primary }}>HSN</th>}
              <th style={{ padding: '12px', textAlign: 'center', fontSize: '10px', fontWeight: '600', color: bw ? '#374151' : colors.primary }}>Qty</th>
              <th style={{ padding: '12px', textAlign: 'right', fontSize: '10px', fontWeight: '600', color: bw ? '#374151' : colors.primary }}>Rate</th>
              {config.content.showDiscountColumn && <th style={{ padding: '12px', textAlign: 'center', fontSize: '10px', fontWeight: '600', color: bw ? '#374151' : colors.primary }}>Disc</th>}
              <th style={{ padding: '12px', textAlign: 'right', fontSize: '10px', fontWeight: '600', color: bw ? '#374151' : colors.primary }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => {
              const amount = item.qty * item.rate * (1 - item.discount / 100);
              return (
                <tr key={idx} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px', color: '#94a3b8' }}>{idx + 1}</td>
                  <td style={{ padding: '12px' }}>
                    <p style={{ fontWeight: '500' }}>{item.name}</p>
                    {config.content.showItemDescription && <p style={{ fontSize: '9px', color: '#94a3b8', marginTop: '2px' }}>{item.desc}</p>}
                  </td>
                  {config.content.showHSN && <td style={{ padding: '12px', color: '#64748b' }}>{item.hsn}</td>}
                  <td style={{ padding: '12px', textAlign: 'center' }}>{item.qty}</td>
                  <td style={{ padding: '12px', textAlign: 'right' }}>₹{item.rate.toLocaleString()}</td>
                  {config.content.showDiscountColumn && <td style={{ padding: '12px', textAlign: 'center', color: bw ? '#374151' : colors.accent }}>{item.discount}%</td>}
                  <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>₹{amount.toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Totals with accent */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
        <div style={{ width: '260px', backgroundColor: '#f8fafc', borderRadius: '8px', padding: '16px', border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '11px' }}>
            <span style={{ color: '#64748b' }}>Subtotal</span><span>₹{subtotal.toLocaleString()}</span>
          </div>
          {config.content.showTaxBreakup && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '10px', color: '#64748b' }}>
                <span>CGST (9%)</span><span>₹{(taxAmount/2).toLocaleString()}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '10px', color: '#64748b' }}>
                <span>SGST (9%)</span><span>₹{(taxAmount/2).toLocaleString()}</span>
              </div>
            </>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', marginTop: '8px', borderTop: `2px solid ${bw ? '#374151' : colors.primary}` }}>
            <span style={{ fontWeight: '700', color: colors.secondary }}>Total Due</span>
            <span style={{ fontWeight: '700', fontSize: '16px', color: bw ? '#1f2937' : colors.primary }}>₹{total.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {config.content.showTotalInWords && (
        <p style={{ fontSize: '10px', color: '#64748b', fontStyle: 'italic', marginBottom: '16px' }}>
          Rupees Twenty Six Thousand Four Hundred Seventy Nine Only
        </p>
      )}

      {config.content.showPaymentDetails && (
        <div style={{ backgroundColor: bw ? '#f1f5f9' : `${colors.primary}08`, borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' }}>
          <p style={{ fontSize: '10px', fontWeight: '600', color: bw ? '#374151' : colors.primary, marginBottom: '4px' }}>💳 Payment Information</p>
          <p style={{ fontSize: '10px', color: '#64748b' }}>Bank: HDFC Bank | Account: 1234567890 | IFSC: HDFC0001234 | UPI: business@upi</p>
        </div>
      )}

      {/* Notes */}
      <p style={{ fontSize: '10px', color: '#64748b', textAlign: 'center', marginBottom: '16px' }}>{config.notes.defaultNotes}</p>

      {config.content.showSignatureSection && (
        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '24px', borderTop: '1px dashed #e2e8f0' }}>
          <div style={{ textAlign: 'center', width: '140px' }}>
            <div style={{ height: '40px' }}></div>
            <div style={{ borderTop: '1px solid #cbd5e1', paddingTop: '4px' }}>
              <p style={{ fontSize: '9px', color: '#94a3b8' }}>Customer Signature</p>
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '10px', color: bw ? '#374151' : colors.primary, fontWeight: '500', marginBottom: '4px' }}>For Billsera Business</p>
            <div style={{ height: '36px' }}></div>
            <div style={{ borderTop: '1px solid #cbd5e1', paddingTop: '4px', width: '140px' }}>
              <p style={{ fontSize: '9px', color: '#94a3b8' }}>Authorized Signatory</p>
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
);

// =====================================================
// THEME 4: CLASSIC ELEGANT
// Centered, serif typography, framed sections, formal
// =====================================================
const ClassicElegantLayout = ({ config, colors, fontFamily, baseFontSize, docLabel, items, subtotal, taxAmount, total, bw }) => (
  <div style={{ width: '595px', minHeight: '842px', fontFamily, fontSize: baseFontSize, backgroundColor: '#fff', padding: '40px', color: colors.secondary, border: `2px solid ${bw ? '#374151' : colors.primary}` }}>
    {/* Header - Centered, Formal */}
    <div style={{ textAlign: 'center', borderBottom: `2px double ${bw ? '#374151' : colors.primary}`, paddingBottom: '20px', marginBottom: '24px' }}>
      <h1 style={{ fontSize: '24px', fontWeight: '400', letterSpacing: '4px', color: bw ? '#1f2937' : colors.primary, margin: '0 0 8px', textTransform: 'uppercase' }}>Billsera</h1>
      <p style={{ fontSize: '10px', color: '#6b7280', letterSpacing: '1px' }}>BUSINESS & CONSULTING SERVICES</p>
      <p style={{ fontSize: '9px', color: '#9ca3af', marginTop: '4px' }}>123 Business Street, City - 400001 | Tel: +91 22 1234 5678</p>
      {config.content.showBusinessGST && <p style={{ fontSize: '9px', color: '#9ca3af' }}>GSTIN: 27XXXXX1234X1ZX</p>}
    </div>

    {/* Document Title - Centered */}
    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
      <span style={{ display: 'inline-block', padding: '8px 32px', border: `1px solid ${bw ? '#374151' : colors.primary}`, fontSize: '12px', fontWeight: '600', letterSpacing: '3px', color: bw ? '#1f2937' : colors.primary }}>
        {docLabel}
      </span>
    </div>

    {/* Invoice Info - Symmetrical */}
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
      <div>
        <p style={{ fontSize: '9px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Invoice To</p>
        <p style={{ fontSize: '12px', fontWeight: '500' }}>ABC Enterprises Pvt. Ltd.</p>
        <p style={{ fontSize: '10px', color: '#6b7280' }}>456 Customer Road</p>
        <p style={{ fontSize: '10px', color: '#6b7280' }}>Mumbai - 400002</p>
        {config.content.showCustomerPhone && <p style={{ fontSize: '10px', color: '#6b7280' }}>Tel: +91 98765 43210</p>}
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ marginBottom: '8px' }}>
          <p style={{ fontSize: '9px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px' }}>Invoice Number</p>
          <p style={{ fontSize: '12px', fontWeight: '500' }}>INV-2024-001234</p>
        </div>
        <div>
          <p style={{ fontSize: '9px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px' }}>Date</p>
          <p style={{ fontSize: '12px', fontWeight: '500' }}>27 December 2024</p>
        </div>
      </div>
    </div>

    {/* Table - Framed with elegant borders */}
    <div style={{ border: `1px solid ${bw ? '#374151' : colors.primary}`, marginBottom: '24px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${bw ? '#374151' : colors.primary}` }}>
            <th style={{ padding: '10px', textAlign: 'center', fontSize: '9px', fontWeight: '600', color: bw ? '#374151' : colors.primary, textTransform: 'uppercase', letterSpacing: '1px', width: '40px' }}>S.No.</th>
            <th style={{ padding: '10px', textAlign: 'left', fontSize: '9px', fontWeight: '600', color: bw ? '#374151' : colors.primary, textTransform: 'uppercase', letterSpacing: '1px', borderLeft: `1px solid ${bw ? '#d1d5db' : colors.primary}30` }}>Particulars</th>
            {config.content.showHSN && <th style={{ padding: '10px', textAlign: 'center', fontSize: '9px', fontWeight: '600', color: bw ? '#374151' : colors.primary, textTransform: 'uppercase', letterSpacing: '1px', borderLeft: `1px solid ${bw ? '#d1d5db' : colors.primary}30` }}>HSN</th>}
            <th style={{ padding: '10px', textAlign: 'center', fontSize: '9px', fontWeight: '600', color: bw ? '#374151' : colors.primary, textTransform: 'uppercase', letterSpacing: '1px', borderLeft: `1px solid ${bw ? '#d1d5db' : colors.primary}30` }}>Qty</th>
            <th style={{ padding: '10px', textAlign: 'right', fontSize: '9px', fontWeight: '600', color: bw ? '#374151' : colors.primary, textTransform: 'uppercase', letterSpacing: '1px', borderLeft: `1px solid ${bw ? '#d1d5db' : colors.primary}30` }}>Rate (₹)</th>
            <th style={{ padding: '10px', textAlign: 'right', fontSize: '9px', fontWeight: '600', color: bw ? '#374151' : colors.primary, textTransform: 'uppercase', letterSpacing: '1px', borderLeft: `1px solid ${bw ? '#d1d5db' : colors.primary}30` }}>Amount (₹)</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => {
            const amount = item.qty * item.rate * (1 - item.discount / 100);
            return (
              <tr key={idx} style={{ borderBottom: `1px solid ${bw ? '#e5e7eb' : colors.primary}20` }}>
                <td style={{ padding: '10px', textAlign: 'center', color: '#6b7280' }}>{idx + 1}.</td>
                <td style={{ padding: '10px', borderLeft: `1px solid ${bw ? '#e5e7eb' : colors.primary}20` }}>
                  <p style={{ fontWeight: '500' }}>{item.name}</p>
                  {config.content.showItemDescription && <p style={{ fontSize: '9px', color: '#9ca3af', fontStyle: 'italic', marginTop: '2px' }}>{item.desc}</p>}
                </td>
                {config.content.showHSN && <td style={{ padding: '10px', textAlign: 'center', color: '#6b7280', borderLeft: `1px solid ${bw ? '#e5e7eb' : colors.primary}20` }}>{item.hsn}</td>}
                <td style={{ padding: '10px', textAlign: 'center', borderLeft: `1px solid ${bw ? '#e5e7eb' : colors.primary}20` }}>{item.qty}</td>
                <td style={{ padding: '10px', textAlign: 'right', borderLeft: `1px solid ${bw ? '#e5e7eb' : colors.primary}20` }}>{item.rate.toLocaleString()}</td>
                <td style={{ padding: '10px', textAlign: 'right', fontWeight: '500', borderLeft: `1px solid ${bw ? '#e5e7eb' : colors.primary}20` }}>{amount.toLocaleString()}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>

    {/* Totals - Framed */}
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
      <div style={{ width: '240px', border: `1px solid ${bw ? '#374151' : colors.primary}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderBottom: `1px solid ${bw ? '#e5e7eb' : colors.primary}20`, fontSize: '10px' }}>
          <span style={{ color: '#6b7280' }}>Sub Total</span><span style={{ fontWeight: '500' }}>₹ {subtotal.toLocaleString()}</span>
        </div>
        {config.content.showTaxBreakup && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', borderBottom: `1px solid ${bw ? '#e5e7eb' : colors.primary}20`, fontSize: '9px', color: '#6b7280' }}>
              <span>Add: CGST @ 9%</span><span>₹ {(taxAmount/2).toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', borderBottom: `1px solid ${bw ? '#e5e7eb' : colors.primary}20`, fontSize: '9px', color: '#6b7280' }}>
              <span>Add: SGST @ 9%</span><span>₹ {(taxAmount/2).toLocaleString()}</span>
            </div>
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', backgroundColor: bw ? '#f3f4f6' : `${colors.primary}10` }}>
          <span style={{ fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '10px' }}>Grand Total</span>
          <span style={{ fontWeight: '700', fontSize: '14px', color: bw ? '#1f2937' : colors.primary }}>₹ {total.toLocaleString()}</span>
        </div>
      </div>
    </div>

    {config.content.showTotalInWords && (
      <p style={{ fontSize: '10px', color: '#6b7280', fontStyle: 'italic', textAlign: 'center', marginBottom: '20px' }}>
        (Rupees Twenty Six Thousand Four Hundred Seventy Nine Only)
      </p>
    )}

    {/* Terms */}
    <div style={{ borderTop: `1px solid ${bw ? '#d1d5db' : colors.primary}30`, paddingTop: '16px', marginBottom: '20px' }}>
      <p style={{ fontSize: '9px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Terms & Conditions</p>
      <p style={{ fontSize: '9px', color: '#6b7280', whiteSpace: 'pre-line' }}>{config.notes.termsAndConditions}</p>
    </div>

    {config.content.showSignatureSection && (
      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '20px' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '9px', color: '#9ca3af', marginBottom: '30px' }}>Receiver&apos;s Signature</p>
          <div style={{ width: '120px', borderTop: `1px solid ${bw ? '#374151' : colors.primary}` }}></div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '10px', fontWeight: '500', marginBottom: '30px' }}>For Billsera</p>
          <div style={{ width: '120px', borderTop: `1px solid ${bw ? '#374151' : colors.primary}` }}>
            <p style={{ fontSize: '9px', color: '#9ca3af', marginTop: '4px' }}>Authorized Signatory</p>
          </div>
        </div>
      </div>
    )}
  </div>
);

// =====================================================
// THEME 5: CREATIVE MODERN
// Asymmetric, vertical totals, stylized, bold
// =====================================================
const CreativeModernLayout = ({ config, colors, fontFamily, baseFontSize, docLabel, items, subtotal, taxAmount, total, bw }) => (
  <div style={{ width: '595px', minHeight: '842px', fontFamily, fontSize: baseFontSize, backgroundColor: '#fff', color: colors.secondary, display: 'flex' }}>
    {/* Left Content Area (70%) */}
    <div style={{ flex: '0 0 70%', padding: '32px' }}>
      {/* Header - Stylized */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', marginBottom: '8px' }}>
          <div style={{ width: '48px', height: '48px', background: bw ? '#1f2937' : `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: '#fff', fontSize: '16px', fontWeight: '800' }}>T</span>
          </div>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: '800', color: bw ? '#1f2937' : colors.primary, margin: 0, lineHeight: 1 }}>billsera</h1>
            <p style={{ fontSize: '9px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '2px' }}>business solutions</p>
          </div>
        </div>
        <p style={{ fontSize: '10px', color: '#6b7280', marginTop: '8px' }}>
          123 Business Street, City - 400001
          {config.content.showBusinessGST && ' • GSTIN: 27XXXXX1234X1ZX'}
        </p>
      </div>

      {/* Document Type Badge */}
      <div style={{ marginBottom: '24px' }}>
        <span style={{ display: 'inline-block', padding: '6px 16px', background: bw ? '#f3f4f6' : `linear-gradient(135deg, ${colors.primary}20, ${colors.accent}20)`, borderRadius: '20px', fontSize: '10px', fontWeight: '700', color: bw ? '#374151' : colors.primary, letterSpacing: '1px' }}>
          {docLabel}
        </span>
        <span style={{ marginLeft: '12px', fontSize: '11px', color: '#6b7280' }}>#INV-2024-001234 • 27 Dec 2024</span>
      </div>

      {/* Bill To - Asymmetric card */}
      <div style={{ backgroundColor: bw ? '#f9fafb' : `${colors.primary}05`, borderRadius: '16px', padding: '20px', marginBottom: '24px', borderLeft: `4px solid ${bw ? '#374151' : colors.primary}` }}>
        <p style={{ fontSize: '9px', color: bw ? '#374151' : colors.accent, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Billed To</p>
        <p style={{ fontSize: '14px', fontWeight: '700', color: colors.secondary }}>ABC Enterprises Pvt. Ltd.</p>
        <p style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px' }}>456 Customer Road, Mumbai - 400002</p>
        <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
          {config.content.showCustomerPhone && <span style={{ fontSize: '10px', color: '#6b7280' }}>📱 +91 98765 43210</span>}
          {config.content.showCustomerEmail && <span style={{ fontSize: '10px', color: '#6b7280' }}>✉️ accounts@abc.com</span>}
        </div>
      </div>

      {/* Items - Modern sectioned rows */}
      <div style={{ marginBottom: '24px' }}>
        <p style={{ fontSize: '9px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>Items</p>
        {items.map((item, idx) => {
          const amount = item.qty * item.rate * (1 - item.discount / 100);
          return (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: idx < items.length - 1 ? '1px dashed #e5e7eb' : 'none' }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: '600', fontSize: '12px' }}>{item.name}</p>
                {config.content.showItemDescription && <p style={{ fontSize: '9px', color: '#9ca3af', marginTop: '2px' }}>{item.desc}</p>}
                <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
                  {config.content.showHSN && <span style={{ fontSize: '9px', color: '#9ca3af' }}>HSN: {item.hsn}</span>}
                  <span style={{ fontSize: '9px', color: '#9ca3af' }}>{item.qty} × ₹{item.rate}</span>
                  {config.content.showDiscountColumn && item.discount > 0 && (
                    <span style={{ fontSize: '9px', color: bw ? '#374151' : colors.accent }}>-{item.discount}%</span>
                  )}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontWeight: '700', fontSize: '13px', color: bw ? '#1f2937' : colors.primary }}>₹{amount.toLocaleString()}</p>
              </div>
            </div>
          );
        })}
      </div>

      {config.content.showTotalInWords && (
        <p style={{ fontSize: '10px', color: '#9ca3af', fontStyle: 'italic', marginBottom: '16px' }}>
          In words: Rupees Twenty Six Thousand Four Hundred Seventy Nine Only
        </p>
      )}

      {/* Notes */}
      <div style={{ backgroundColor: '#f9fafb', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
        <p style={{ fontSize: '10px', color: '#6b7280' }}>{config.notes.defaultNotes}</p>
      </div>

      {config.content.showPaymentDetails && (
        <div style={{ fontSize: '9px', color: '#6b7280' }}>
          <p style={{ fontWeight: '600', marginBottom: '4px' }}>Payment Details</p>
          <p>HDFC Bank | A/C: 1234567890 | IFSC: HDFC0001234</p>
        </div>
      )}
    </div>

    {/* Right Vertical Totals Panel (30%) */}
    <div style={{ flex: '0 0 30%', background: bw ? '#1f2937' : `linear-gradient(180deg, ${colors.primary}, ${colors.secondary})`, color: '#fff', padding: '32px 20px', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '2px', opacity: 0.7, marginBottom: '24px' }}>Summary</p>
        
        <div style={{ marginBottom: '16px' }}>
          <p style={{ fontSize: '10px', opacity: 0.7 }}>Subtotal</p>
          <p style={{ fontSize: '16px', fontWeight: '600' }}>₹{subtotal.toLocaleString()}</p>
        </div>

        {config.content.showTaxBreakup && (
          <>
            <div style={{ marginBottom: '8px' }}>
              <p style={{ fontSize: '9px', opacity: 0.7 }}>CGST (9%)</p>
              <p style={{ fontSize: '12px' }}>₹{(taxAmount/2).toLocaleString()}</p>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <p style={{ fontSize: '9px', opacity: 0.7 }}>SGST (9%)</p>
              <p style={{ fontSize: '12px' }}>₹{(taxAmount/2).toLocaleString()}</p>
            </div>
          </>
        )}

        <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '16px', marginTop: '16px' }}>
          <p style={{ fontSize: '10px', opacity: 0.7, marginBottom: '4px' }}>Total Amount</p>
          <p style={{ fontSize: '28px', fontWeight: '800' }}>₹{total.toLocaleString()}</p>
        </div>
      </div>

      {/* Status Badge */}
      <div style={{ marginTop: 'auto', paddingTop: '24px' }}>
        <div style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
          <p style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '1px' }}>UNPAID</p>
          <p style={{ fontSize: '9px', opacity: 0.7, marginTop: '2px' }}>Due: 26 Jan 2025</p>
        </div>
      </div>

      {config.content.showQRCode && (
        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <div style={{ width: '60px', height: '60px', backgroundColor: '#fff', borderRadius: '8px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: '8px', color: '#6b7280' }}>QR</span>
          </div>
          <p style={{ fontSize: '8px', marginTop: '4px', opacity: 0.7 }}>Scan to pay</p>
        </div>
      )}

      {config.content.showSignatureSection && (
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <div style={{ height: '30px' }}></div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.3)', paddingTop: '4px' }}>
            <p style={{ fontSize: '8px', opacity: 0.7 }}>Authorized</p>
          </div>
        </div>
      )}
    </div>
  </div>
);

export default TemplateStudioPage;
